import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { KpiCard } from "@/components/dashboard/shared/KpiCard";
import { KpiGrid } from "@/components/dashboard/shared/KpiGrid";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { EmptyState } from "@/components/dashboard/shared/EmptyState";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { InvoiceAnalytics, InvoiceListResponse, InvoiceStatus } from "@/types/invoice";
import {
  RiAddLine,
  RiSearchLine,
  RiFileTextLine,
  RiMoneyDollarCircleLine,
  RiTimeLine,
  RiCheckboxCircleLine,
  RiHistoryLine,
  RiCalendarLine,
  RiCloseLine,
} from "@remixicon/react";

// ── Time period presets ────────────────────────────────────────────────────────

type PeriodPreset = "all" | "today" | "week" | "month" | "quarter" | "year" | "custom";

interface DateRange { from: string; to: string }

function getPresetRange(preset: PeriodPreset): DateRange | null {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = fmt(now);

  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "week": {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay()); // Sunday
      return { from: fmt(start), to: today };
    }
    case "month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: fmt(start), to: today };
    }
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1);
      return { from: fmt(start), to: today };
    }
    case "year": {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: fmt(start), to: today };
    }
    default:
      return null;
  }
}

const PERIOD_OPTIONS: { label: string; value: PeriodPreset }[] = [
  { label: "All time", value: "all" },
  { label: "Today", value: "today" },
  { label: "This week", value: "week" },
  { label: "This month", value: "month" },
  { label: "This quarter", value: "quarter" },
  { label: "This year", value: "year" },
  { label: "Custom", value: "custom" },
];

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "All", value: "all" },
  { label: "Draft", value: "draft" },
  { label: "Sent", value: "sent" },
  { label: "Partial", value: "partial" },
  { label: "Paid", value: "paid" },
  { label: "Overdue", value: "overdue" },
  { label: "Cancelled", value: "cancelled" },
];

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  partial: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export function InvoicesPage() {
  const { storeId, currentStore } = useCurrentStore();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [period, setPeriod] = useState<PeriodPreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [page, setPage] = useState(1);

  const currency = currentStore?.currency || "NGN";
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(n);

  // Resolve active date range from preset or custom inputs
  const activeDateRange: DateRange | null =
    period === "custom"
      ? customFrom || customTo
        ? { from: customFrom, to: customTo }
        : null
      : getPresetRange(period);

  const resetPeriod = () => {
    setPeriod("all");
    setCustomFrom("");
    setCustomTo("");
    setPage(1);
  };

  const { data: analytics, isLoading: analyticsLoading } =
    useQuery<InvoiceAnalytics>({
      queryKey: ["invoice-analytics", storeId],
      queryFn: async () => {
        const { data } = await api.get("/api/invoices/analytics");
        return data;
      },
      enabled: !!storeId,
    });

  const { data: invoices, isLoading } = useQuery<InvoiceListResponse>({
    queryKey: ["invoices", storeId, search, statusFilter, period, customFrom, customTo, page],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        search,
        status: statusFilter,
        page,
        limit: 15,
      };
      if (activeDateRange?.from) params.dateFrom = activeDateRange.from;
      if (activeDateRange?.to) params.dateTo = activeDateRange.to;
      const { data } = await api.get("/api/invoices", { params });
      return data;
    },
    enabled: !!storeId,
  });

  const hasActiveFilter = period !== "all";

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Invoices" />
      <PageHeader
        title="Invoices"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild className="rounded-full font-semibold">
              <Link to="/dashboard/invoices/payments">
                <RiHistoryLine className="h-4 w-4 mr-1.5" />
                Payment history
              </Link>
            </Button>
            <Button size="sm" asChild className="rounded-full font-semibold shadow-none">
              <Link to="/dashboard/invoices/new">
                <RiAddLine className="h-4 w-4 mr-1.5" />
                New invoice
              </Link>
            </Button>
          </div>
        }
      />

      {/* Analytics KPIs */}
      <KpiGrid loading={analyticsLoading} skeletonCount={4} columns="grid-cols-2 lg:grid-cols-4">
        {analytics && (
          <>
            <KpiCard
              label="Total Invoices"
              value={analytics.totalInvoices ?? 0}
              icon={<RiFileTextLine />}
            />
            <KpiCard
              label="Revenue Collected"
              value={analytics.totalRevenue ?? 0}
              currency={currency}
              icon={<RiMoneyDollarCircleLine />}
            />
            <KpiCard
              label="Outstanding"
              value={analytics.outstandingAmount ?? 0}
              currency={currency}
              icon={<RiTimeLine />}
            />
            <KpiCard
              label="Paid"
              value={analytics.paidInvoices ?? 0}
              icon={<RiCheckboxCircleLine />}
            />
          </>
        )}
      </KpiGrid>

      {/* ── Filters ── */}
      <div className="space-y-3">
        {/* Row 1: search + status */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by invoice # or customer name…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className={`${dashboardStyles.filterInput} pl-9`}
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_OPTIONS.map((s) => (
              <Button
                key={s.value}
                size="sm"
                variant={statusFilter === s.value ? "default" : "outline"}
                onClick={() => { setStatusFilter(s.value); setPage(1); }}
                className="h-9 rounded-full"
              >
                {s.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Row 2: time period presets */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
            <RiCalendarLine className="h-3.5 w-3.5" />
            <span>Period:</span>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {PERIOD_OPTIONS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => { setPeriod(p.value); setPage(1); }}
                className="h-8 rounded-full text-xs"
              >
                {p.label}
              </Button>
            ))}
          </div>
          {hasActiveFilter && (
            <button
              onClick={resetPeriod}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1"
            >
              <RiCloseLine className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>

        {/* Row 3: custom date inputs (only when "Custom" is selected) */}
        {period === "custom" && (
          <div className="flex flex-col sm:flex-row items-center gap-2 pl-1">
            <span className="text-xs text-muted-foreground shrink-0">From</span>
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => { setCustomFrom(e.target.value); setPage(1); }}
              className="h-8 text-sm w-full sm:w-40"
            />
            <span className="text-xs text-muted-foreground shrink-0">To</span>
            <Input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => { setCustomTo(e.target.value); setPage(1); }}
              className="h-8 text-sm w-full sm:w-40"
            />
            {activeDateRange && (
              <span className="text-xs text-muted-foreground">
                {activeDateRange.from || "…"} → {activeDateRange.to || "…"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Invoice List */}
      <SectionCard variant="list">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">All Invoices</p>
          {!isLoading && invoices?.data?.length ? (
            <span className="text-xs text-muted-foreground">
              {invoices.pagination.totalItems} total
            </span>
          ) : null}
        </div>

        {isLoading ? (
          <div className={dashboardStyles.listContainer}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-transparent"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="w-7 h-7 rounded-lg shrink-0" />
                  <div>
                    <Skeleton className="h-3 w-28 mb-1.5" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                </div>
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : !invoices?.data?.length ? (
          <EmptyState
            compact
            icon={<RiFileTextLine className="h-4 w-4 text-muted-foreground" />}
            title="No invoices yet"
            description="Create your first invoice to get started."
            action={
              <Button asChild size="sm" variant="outline" className="h-7 text-xs px-3">
                <Link to="/dashboard/invoices/new">
                  <RiAddLine className="h-3.5 w-3.5 mr-1.5" />
                  New invoice
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className={dashboardStyles.listContainer}>
              {invoices.data.map((inv) => (
                <Link
                  key={inv._id}
                  to={`/dashboard/invoices/${inv._id}`}
                  className={dashboardStyles.listItem}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={dashboardStyles.listItemIcon}>
                      <RiFileTextLine className={dashboardStyles.listItemIconSvg} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm leading-tight truncate">
                        {inv.invoiceNumber}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {inv.client?.name}
                        {inv.dueDate
                          ? ` · Due ${new Date(inv.dueDate).toLocaleDateString("en-NG", {
                              day: "numeric",
                              month: "short",
                            })}`
                          : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <div className="font-semibold text-sm font-heading leading-tight">
                      {fmt(inv.total)}
                    </div>
                    <Badge
                      className={`mt-0.5 capitalize ${STATUS_COLORS[inv.status as InvoiceStatus] || "bg-gray-100"}`}
                    >
                      {inv.status}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {invoices.pagination.totalPages > 1 && (
              <div className={dashboardStyles.paginationBar}>
                <span>
                  Page {invoices.pagination.currentPage} of{" "}
                  {invoices.pagination.totalPages} ({invoices.pagination.totalItems} invoices)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className={dashboardStyles.paginationBtn}
                    disabled={!invoices.pagination.hasPrevPage}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={dashboardStyles.paginationBtn}
                    disabled={!invoices.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}
